import {CSSProperties, ElementType, HTMLAttributes, JSX, ReactNode} from "react";

import classNames from "classnames";

import "./styles/typography.scss";

export type FontSize = 8 | 10 | 11 | 12 | 14 | 16 | 18 | 20 | 22 | 24 | 26 | 28 | 32 | 40 | 48 | 58;
export type FontWeight = 400 | 500 | 600 | 700;
export type FontColor = "black" | "title" | "paragraph" | "paragraph2" | "primary" | "white" | "error";
export type LineHeight = 100 | 110 | 120 | 140 | 150 | 200;

export type ResponsiveSize = FontSize | {desktop: FontSize; mobile: FontSize};
export type ResponsiveWeight = FontWeight | {desktop: FontWeight; mobile: FontWeight};

interface TypographyProps extends HTMLAttributes<HTMLElement> {
    as: ElementType;
    children?: ReactNode;
    innerHTML?: string;
    className?: string;
    style?: CSSProperties;
    size?: ResponsiveSize;
    weight?: ResponsiveWeight;
    color?: FontColor;
    lineHeight?: LineHeight;
    options?: TypographyOptions;
}

type TypographyOption = "uppercase" | "capitalize" | "underline" | "fit-content";
type TypographyOptions = Array<TypographyOption>;

// Helper functions that return class names
const getSizeClasses = (size?: ResponsiveSize): Array<string> => {
    const classes: Array<string> = [];

    if (size) {
        if (typeof size === "number") {
            classes.push(`t-s-${size}`);
        } else {
            if (size.desktop) classes.push(`t-s-${size.desktop}`);
            if (size.mobile) classes.push(`t-sm-${size.mobile}`);
        }
    }

    return classes;
};

const getWeightClasses = (weight?: ResponsiveWeight): Array<string> => {
    const defaultWeight = 500;
    const desktopWeight = !weight ? defaultWeight : typeof weight === "number" ? weight : weight.desktop;
    const classes = [`t-w-${desktopWeight}`];

    if (weight && typeof weight !== "number" && weight.mobile) {
        classes.push(`t-wm-${weight.mobile}`);
    }

    return classes;
};

const getColorClass = (color?: FontColor): string => {
    return `t-c-${color ?? "black"}`;
};

const getLineHeightClass = (lineHeight?: LineHeight): string => {
    return `t-lh-${lineHeight ?? 150}`;
};

export function Typography({
    as,
    children,
    innerHTML,
    size,
    weight,
    color,
    lineHeight,
    className = "",
    style,
    options,
    ...rest
}: TypographyProps): JSX.Element | null {
    if (!children && !innerHTML) {
        return null;
    }
    const Component = as;

    const optionClasses = options?.map((option) => `t-${option}`) ?? [];

    const props = {
        className: classNames(
            "t",
            ...getSizeClasses(size),
            ...getWeightClasses(weight),
            getColorClass(color),
            getLineHeightClass(lineHeight),
            ...optionClasses,
            className,
        ),
        style,
        ...(innerHTML ? {dangerouslySetInnerHTML: {__html: innerHTML}} : {}),
        ...rest,
    };

    if (innerHTML) {
        return <Component {...props} />;
    }

    return <Component {...props}>{children}</Component>;
}
